// Money. Defined once, referenced by every DTO that carries an amount.
//
// Spec: spec/06-billing-pricing-and-payments.md §1, spec/08-api-design.md §2.3 and §3, CLAUDE.md.
//
// An amount is an integer in the currency's minor unit plus an ISO 4217 code: `{ amount: 15000,
// currency: 'INR' }` is ₹150.00. Never a float, never a string of rupees — not in a DTO, not in a
// column, not in a variable that is only "for display".
//
// The arithmetic (adding, allocating, rounding, converting) lives in `packages/domain`. This file
// owns the shape and the guard, and nothing else: a package that both defines money and does sums
// on it is a package two other packages have to depend on for the wrong reason.
import { z } from "../zod.js";
import { CurrencyCodeSchema } from "./scalars.js";
/**
 * The cap on a single amount: 10^13 minor units, which is ₹10,000,00,00,000 — comfortably below
 * `Number.MAX_SAFE_INTEGER` (2^53 − 1 ≈ 9.007 × 10^15), so a JSON number never silently loses a
 * paisa on the way through a client. Postgres stores the column as `bigint`; the API guarantees
 * the range (spec/06 §1.1).
 */
export const MONEY_MAX_MINOR_UNITS = 10_000_000_000_000;
/** Amounts are signed: a ledger credit, a refund line and a negative adjustment are all money. */
export const MONEY_MIN_MINOR_UNITS = -MONEY_MAX_MINOR_UNITS;
/**
 * A fresh amount schema each time, so a variant's tighter bound is really in the generated
 * document rather than hidden behind a `$ref` to the unbounded one.
 */
const moneyAmount = (minimum = MONEY_MIN_MINOR_UNITS) => z.int().min(minimum).max(MONEY_MAX_MINOR_UNITS);
export const MoneySchema = z
    .object({
    amount: moneyAmount().openapi({
        description: "An integer number of the currency's minor units.",
        example: 15000,
    }),
    currency: CurrencyCodeSchema,
})
    .openapi('Money', {
    description: "An amount in a currency's minor unit. { amount: 15000, currency: 'INR' } is ₹150.00.",
});
/** Money that cannot be negative: a price, a tender, a wallet balance. */
export const NonNegativeMoneySchema = z
    .object({ amount: moneyAmount(0), currency: CurrencyCodeSchema })
    .openapi('NonNegativeMoney', { description: 'Money that may be zero but never negative.' });
/** Money that must be a real movement: a payment, a top-up, a refund amount. */
export const PositiveMoneySchema = z
    .object({ amount: moneyAmount(1), currency: CurrencyCodeSchema })
    .openapi('PositiveMoney', { description: 'Money strictly greater than zero.' });
/**
 * The guard. Narrows an unknown value to `Money` without throwing — for the boundaries Zod does
 * not own, such as a row coming back from a driver or a value read out of a JSON column.
 */
export function isMoney(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (typeof candidate['amount'] === 'number' &&
        Number.isSafeInteger(candidate['amount']) &&
        candidate['amount'] >= MONEY_MIN_MINOR_UNITS &&
        candidate['amount'] <= MONEY_MAX_MINOR_UNITS &&
        typeof candidate['currency'] === 'string' &&
        /^[A-Z]{3}$/.test(candidate['currency']));
}
/** The same guard, for the places where carrying on with a bad amount is worse than stopping. */
export function assertMoney(value, context = 'value') {
    if (!isMoney(value)) {
        throw new TypeError(`${context} is not Money: expected { amount: integer within ±${MONEY_MAX_MINOR_UNITS}, currency: ISO 4217 }.`);
    }
}
/** True when two amounts can be compared or summed at all. Currency mixing is a bug, never a cast. */
export function isSameCurrency(a, b) {
    return a.currency === b.currency;
}
//# sourceMappingURL=money.js.map